import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { TmsWebBaseEditCreateService } from '../base';
import {
  CalculateDeliveryDateRequest,
  CalculateDeliveryDateResponse,
  CollectionLookupResult,
  ConfirmSalesOrder,
  DefaultQuantity,
  DeliveryDate,
  Fulfilment,
  OrderLineStatusCheck,
  PriceFromPriceListRequest,
  PriceFromPriceListResponse,
  ProductsForCreatePurchaseOrder,
  PurchaseOrderSalesAllocation,
  SalesOrderAllocations,
  SalesOrderFromPreviousIds,
  SalesOrderHeaderLine,
  SalesOrderLine,
  SalesOrderLineAllocationsSummaryTop,
  SalesOrderLineBillOfMaterials,
  SalesOrderLineCommissionDetails,
  SalesOrderLineDetails,
  SalesOrderLineForCompleting,
  SalesOrderLinePreviousOrders,
  SalesOrderLinePurchaseAllocationsSummaryTop,
  SalesOrderLineQuickCreate,
  SalesOrderLineSummaryTop,
  SalesOrderLineWhatsHappened,
  SalesOrderMultiProductCreate,
  UpdateAllocationsRequest,
  UpdateAllocationsResult,
} from '../../models';

@Injectable({
  providedIn: 'root',
})
export class SalesOrderLineService extends TmsWebBaseEditCreateService<SalesOrderLine, SalesOrderLineDetails> {
  public refreshDetails = new Subject<void>();

  constructor() {
    super('SalesOrderLine', $localize`Sales Order Line`);
  }

  getForSalesOrderHeader(id: string): Observable<SalesOrderHeaderLine[]> {
    return this.httpService.get<SalesOrderHeaderLine[]>(`${this.apiUrl}/SalesOrderHeader?id=${id}`);
  }

  // The Sales Order's What's Happened tab: every Lines-tab row plus where the line has got to.
  getWhatsHappened(salesOrderHeaderId: string): Observable<SalesOrderLineWhatsHappened[]> {
    return this.httpService.get<SalesOrderLineWhatsHappened[]>(
      `${this.apiUrl}/WhatsHappened?salesOrderHeaderId=${salesOrderHeaderId}`,
    );
  }

  getFulfilment(salesOrderHeaderId: string): Observable<Fulfilment> {
    return this.httpService.get<Fulfilment>(`${this.apiUrl}/Fulfilment?salesOrderHeaderId=${salesOrderHeaderId}`);
  }

  updateAllocations(request: UpdateAllocationsRequest): Observable<UpdateAllocationsResult> {
    return this.httpService.post<UpdateAllocationsResult>(`${this.apiUrl}/UpdateAllocations`, request);
  }

  getConfirmSalesOrderLine(salesOrderHeaderId: string): Observable<ConfirmSalesOrder[]> {
    return this.httpService.get<ConfirmSalesOrder[]>(
      `${this.apiUrl}/GetConfirmSalesOrderLines?salesOrderHeaderId=${salesOrderHeaderId}`,
    );
  }

  getForAutoCreatePO(id: string): Observable<ProductsForCreatePurchaseOrder[]> {
    return this.httpService.get<ProductsForCreatePurchaseOrder[]>(
      `${this.apiUrl}/SalesOrderLinesForAutoCreatePO?id=${id}`,
    );
  }

  getSalesOrderBillOfMaterials(salesOrderLineId: string): Observable<SalesOrderLineBillOfMaterials[]> {
    return this.httpService.get<SalesOrderLineBillOfMaterials[]>(
      `${this.apiUrl}/GetSalesOrderBillOfMaterials?salesOrderLineId=${salesOrderLineId}`,
    );
  }

  getOutstandingOrderLines(): Observable<SalesOrderLineForCompleting[]> {
    return this.httpService.get<SalesOrderLineForCompleting[]>(`${this.apiUrl}/SalesOrderLinesForCompleting`);
  }

  getForSalesInvoiceCommission(): Observable<SalesOrderLineCommissionDetails[]> {
    return this.httpService.get<SalesOrderLineCommissionDetails[]>(`${this.apiUrl}/GetForSalesInvoiceCommission`);
  }

  salesOrderAllocations(id: string): Observable<SalesOrderAllocations> {
    return this.httpService.get<SalesOrderAllocations>(`${this.apiUrl}/SalesOrderAllocation?id=${id}`);
  }

  purchaseOrderSalesAllocation(id: string): Observable<PurchaseOrderSalesAllocation[]> {
    return this.httpService.get<PurchaseOrderSalesAllocation[]>(`${this.apiUrl}/PurchaseOrderSalesAllocation?id=${id}`);
  }

  summaryTop(id: string): Observable<SalesOrderLineSummaryTop> {
    return this.httpService.get<SalesOrderLineSummaryTop>(`${this.apiUrl}/SalesOrderLineSummaryTop?id=${id}`);
  }

  allocationsSummaryTop(id: string): Observable<SalesOrderLineAllocationsSummaryTop> {
    return this.httpService.get<SalesOrderLineAllocationsSummaryTop>(`${this.apiUrl}/AllocationsSummaryTop?id=${id}`);
  }

  purchaseAllocationsSummaryTop(id: string): Observable<SalesOrderLinePurchaseAllocationsSummaryTop> {
    return this.httpService.get<SalesOrderLinePurchaseAllocationsSummaryTop>(
      `${this.apiUrl}/PurchaseAllocationsSummaryTop?id=${id}`,
    );
  }

  getPreviousSalesOrders(salesOrderHeaderId: string, search: string): Observable<SalesOrderLinePreviousOrders[]> {
    return this.httpService.get<SalesOrderLinePreviousOrders[]>(
      `${this.apiUrl}/PreviousSalesOrders?salesOrderHeaderId=${salesOrderHeaderId}&search=${search}`,
    );
  }

  getCheckStatusChangeAllowed(salesOrderLineId: string, newStatusId: string): Observable<OrderLineStatusCheck> {
    return this.httpService.get<OrderLineStatusCheck>(
      `${this.apiUrl}/CheckStatusChangeAllowed?salesOrderLineId=${salesOrderLineId ?? ''}&newStatusID=${newStatusId}`,
    );
  }

  getReservationExpiryDate(calculate: boolean): Observable<Date> {
    return this.httpService.get<Date>(`${this.apiUrl}/GetReservationExpiryDate?calculate=${calculate}`);
  }

  getShowPurchaseContract(id: string): Observable<boolean> {
    return this.httpService.get<boolean>(`${this.apiUrl}/GetShowPurchaseContract?id=${id}`);
  }

  quickCreate(salesOrderLineQuickCreate: SalesOrderLineQuickCreate): Observable<SalesOrderLineQuickCreate> {
    return this.httpService.post<SalesOrderLineQuickCreate>(`${this.apiUrl}/QuickCreate`, salesOrderLineQuickCreate);
  }

  collectionLookup(customerId: string, productKey: string): Observable<CollectionLookupResult> {
    return this.httpService.get<CollectionLookupResult>(
      `${this.apiUrl}/CollectionLookup/${customerId}/${encodeURIComponent(productKey)}`,
    );
  }

  deliveryDate(model: DeliveryDate): Observable<DeliveryDate> {
    return this.httpService.post<DeliveryDate>(`${this.apiUrl}/DeliveryDate`, model);
  }

  getPriceFromPriceList(model: PriceFromPriceListRequest): Observable<PriceFromPriceListResponse> {
    return this.httpService.post<PriceFromPriceListResponse>(`${this.apiUrl}/PriceFromPriceList`, model);
  }

  getCalculateDeliveryDate(model: CalculateDeliveryDateRequest): Observable<CalculateDeliveryDateResponse> {
    return this.httpService.post<CalculateDeliveryDateResponse>(`${this.apiUrl}/CalculateDeliveryDate`, model);
  }

  createFromPreviousOrders(salesOrderHeaderId: string, ids: SalesOrderFromPreviousIds[]): Observable<any> {
    return this.httpService.post(`${this.apiUrl}/CreateFromPreviousOrders`, { salesOrderHeaderId, ids });
  }

  completeOrderLines(ids: string[]): Observable<any> {
    return this.httpService.post<any>(`${this.apiUrl}/CompleteOrderLines`, { ids });
  }

  updateOrderLineStatus(id: string): Observable<any> {
    return this.httpService.post<any>(`${this.apiUrl}/UpdateOrderLineStatus`, { id });
  }

  amendOrder(model: SalesOrderHeaderLine): Observable<any> {
    return this.httpService.post(`${this.apiUrl}/AmendOrder`, model);
  }

  updateCustomerOrder(salesOrderHeaderId: string, purchaseOrderLineId: string): Observable<any> {
    return this.httpService.post(`${this.apiUrl}/UpdateCustomerOrder`, { salesOrderHeaderId, purchaseOrderLineId });
  }

  // The quantity a new line should start with for what has been picked so far - the API owns
  // the rule (order type SAMPLE / product type SAMPLE / variant override SAMPLE -> the order
  // type's Default Quantity). Screens apply it only while the quantity is still empty.
  getDefaultQuantity(
    salesOrderHeaderId: string,
    productId?: string,
    productTypeVariantId?: string,
  ): Observable<DefaultQuantity> {
    const params = new URLSearchParams({ salesOrderHeaderId });
    if (productId) {
      params.set('productId', productId);
    }
    if (productTypeVariantId) {
      params.set('productTypeVariantId', productTypeVariantId);
    }
    return this.httpService.get<DefaultQuantity>(`${this.apiUrl}/DefaultQuantity?${params.toString()}`);
  }

  createMultiProductOrderLines(salesOrderHeaderId: string, salesOrderMultiProducts: SalesOrderMultiProductCreate[]) {
    return this.httpService.post(`${this.apiUrl}/CreateMultiProductOrderLines`, {
      salesOrderHeaderId,
      salesOrderMultiProducts,
    });
  }
}
